import { useState } from "react";
import { toast } from "sonner";
import Logo from "@/components/site/Logo";
import { Mail, Phone, MapPin, Linkedin, Instagram, Send } from "lucide-react";
import { CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_HREF } from "@/lib/api";

export default function Footer() {
  const [newsletter, setNewsletter] = useState("");

  const subscribe = (e) => {
    e.preventDefault();
    const address = newsletter.trim();
    if (!address) return;
    // Kein Newsletter-Backend: Anmeldung als vorbereitete E-Mail übergeben.
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
      "Newsletter-Anmeldung"
    )}&body=${encodeURIComponent(
      `Bitte nehmen Sie mich in den Sonnenwerk-Newsletter auf.\n\nE-Mail: ${address}`
    )}`;
    toast.success("Ihr E-Mail-Programm öffnet sich zur Bestätigung.");
    setNewsletter("");
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#2A3B32] text-white relative overflow-hidden"
    >
      <div className="absolute -top-32 -right-20 w-[28rem] h-[28rem] sun-ring opacity-50" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 md:py-20">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-x-6 gap-y-10">
          <div className="col-span-2 md:col-span-5">
            <Logo variant="light" />
            <p className="mt-5 text-white/70 text-sm max-w-sm leading-relaxed">
              Photovoltaik aus der Schweiz – für Privat, Gewerbe und Industrie.
              Planung, Installation und Service aus einer Hand.
            </p>

            <div className="mt-7 flex gap-3">
              <a
                href="#"
                aria-label="LinkedIn"
                data-testid="footer-social-linkedin"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#EAB308] hover:text-[#1A1D20] flex items-center justify-center transition-colors"
              >
                <Linkedin size={18} />
              </a>
              <a
                href="#"
                aria-label="Instagram"
                data-testid="footer-social-instagram"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#EAB308] hover:text-[#1A1D20] flex items-center justify-center transition-colors"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>

          <div className="col-span-1 md:col-span-3">
            <div className="text-xs uppercase tracking-widest text-[#EAB308] font-semibold">
              Leistungen
            </div>
            <ul className="mt-4 space-y-2.5 text-sm text-white/75">
              <li><a href="#leistungen" className="hover:text-white">PV-Anlagen Privat</a></li>
              <li><a href="#leistungen" className="hover:text-white">PV-Anlagen Gewerbe</a></li>
              <li><a href="#leistungen" className="hover:text-white">Stromspeicher</a></li>
              <li><a href="#leistungen" className="hover:text-white">Wallboxen</a></li>
              <li><a href="#rechner" className="hover:text-white">Solarrechner</a></li>
              <li><a href="#stimmen" className="hover:text-white">Kundenstimmen</a></li>
            </ul>
          </div>

          <div className="col-span-1 md:col-span-4">
            <div className="text-xs uppercase tracking-widest text-[#EAB308] font-semibold">
              Kontakt
            </div>
            <ul className="mt-4 space-y-3 text-sm text-white/75">
              <li className="flex items-start gap-3">
                <Phone size={16} className="mt-0.5 text-[#EAB308]" aria-hidden="true" />
                <a href={CONTACT_PHONE_HREF} className="hover:text-white">{CONTACT_PHONE}</a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={16} className="mt-0.5 text-[#EAB308]" aria-hidden="true" />
                <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-white">{CONTACT_EMAIL}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={16} className="mt-0.5 text-[#EAB308]" aria-hidden="true" />
                <span>Industriestrasse 24<br />8005 Zürich · Schweiz</span>
              </li>
            </ul>
          </div>
        </div>

        <form
          onSubmit={subscribe}
          data-testid="footer-newsletter"
          className="mt-14 rounded-2xl bg-white/[0.06] border border-white/10 p-6 md:p-7 grid grid-cols-1 md:grid-cols-12 gap-5 items-center"
        >
          <div className="md:col-span-6">
            <div className="font-display font-semibold text-lg">
              Solar-Update abonnieren
            </div>
            <p className="mt-1.5 text-sm text-white/65">
              Viermal im Jahr: Förderänderungen, Strompreise und Praxisbeispiele
              aus der Schweiz. Kein Verkaufsdruck, jederzeit abbestellbar.
            </p>
          </div>
          <div className="md:col-span-6 flex flex-col sm:flex-row gap-2">
            <label htmlFor="newsletter-email" className="sr-only">
              E-Mail-Adresse für den Newsletter
            </label>
            <input
              id="newsletter-email"
              type="email"
              required
              value={newsletter}
              onChange={(e) => setNewsletter(e.target.value)}
              placeholder="ihre@adresse.ch"
              data-testid="footer-newsletter-input"
              className="flex-1 h-12 px-4 rounded-full bg-[#1A1D20]/40 border border-white/15 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#EAB308]"
            />
            <button
              type="submit"
              data-testid="footer-newsletter-submit"
              className="h-12 px-5 rounded-full bg-[#EAB308] text-[#1A1D20] font-semibold text-sm inline-flex items-center gap-2 hover:bg-[#CA9A06] transition-colors"
            >
              Abonnieren <Send size={15} aria-hidden="true" />
            </button>
          </div>
        </form>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row gap-3 items-start md:items-center justify-between text-xs text-white/55">
          <div>© {new Date().getFullYear()} Sonnenwerk AG. Alle Rechte vorbehalten.</div>
          <div className="flex gap-5">
            <a href="#" className="hover:text-white">Impressum</a>
            <a href="#" className="hover:text-white">Datenschutz</a>
            <a href="#" className="hover:text-white">AGB</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
